import debounce from "lodash.debounce";
import { apiCaller } from "./ApiCaller";
import { useCallback, useContext, useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import MainContext from "src/context/MainContext";
import axios from "axios";
import { buildRefundTagQuery } from "src/utils/refundTagQuery";
import { filenameFromContentDisposition } from "src/utils/csvDownload";
import { unwrapCalendarPayload } from "src/features/calenderDaily/bookingPaymentSplitSave";

export const getClubId = () => {
  const user_data = JSON.parse(
    localStorage.getItem("user") || JSON.stringify({})
  );

  if (user_data?.role?.name === "SUPER_ADMIN") {
    const club_id = JSON.parse(
      localStorage.getItem("Club_id") || JSON.stringify({})
    );
    return club_id;
  } else {
    let club_id = user_data?.club_id;
    return club_id;
  }
};

export const useGetClubId = () => {
  const { LoginData } = useContext(MainContext);
  const getClubId = () => {
    if (LoginData?.role?.name === "SUPER_ADMIN") {
      const club_id = JSON.parse(
        localStorage.getItem("Club_id") || JSON.stringify({})
      );
      return club_id;
    } else {
      let club_id = LoginData?.club?.id;
      return club_id;
    }
  };

  const club_id = getClubId();
  return club_id;
};

const intialValue = {
  view_permissions: false,
  edit_permissions: false,
  sales: false,
  occupancy: false,
  customers: false,
  payments_page: false,
  customers_page: false,
  services_page: false,
  pos_page: true,
  coupon_page: true,
  court_setting_page: true,
  automation_page: true,
  email_notification_page: true,
  push_notification_page: true,
  level_permission: false,
  wallet_permission: false,
  payment_date_permission: false,
  make_payment_permission: true,
  edit_coupon: true,
  sport_permission: []
};

export const useGetPermissions = () => {
  const { LoginData } = useContext(MainContext);
  const LocalPerm: any = localStorage.getItem("permission");
  const LocalPermission: any = JSON.parse(LocalPerm);
  if (LoginData?.role?.name === "STAFF") {
    const permission = LoginData?.otherPermissions || intialValue;

    // Handle backward compatibility: default new page permissions to true if undefined
    const backwardCompatiblePermission = {
      ...permission,
      make_payment_permission: permission.make_payment_permission !== undefined ? permission.make_payment_permission : true,
      pos_page: permission.pos_page !== undefined ? permission.pos_page : true,
      coupon_page: permission.coupon_page !== undefined ? permission.coupon_page : true,
      court_setting_page: permission.court_setting_page !== undefined ? permission.court_setting_page : true,
      automation_page: permission.automation_page !== undefined ? permission.automation_page : true,
      email_notification_page: permission.email_notification_page !== undefined ? permission.email_notification_page : true,
      push_notification_page: permission.push_notification_page !== undefined ? permission.push_notification_page : true,
    };

    return backwardCompatiblePermission;
  } else {
    return { edit_permissions: true, role: "OTHER" };
  }
};

export const Permission = () => {
  const user_data = JSON.parse(
    localStorage.getItem("user") || JSON.stringify({})
  );
  if (user_data?.role?.name === "STAFF") {
    const permission = JSON.parse(
      localStorage.getItem("permission") || JSON.stringify({})
    );
    return permission?.otherPermissions;
  } else {
    return { edit_permissions: true, role: "OTHER" };
  }
};

export const getSportPermissions = () => {
  const user_data = JSON.parse(
    localStorage.getItem("user") || JSON.stringify({})
  );

  // If STAFF, return their sport permissions, otherwise return null (no restrictions)
  if (user_data?.role?.name === "STAFF") {
    const permission = JSON.parse(
      localStorage.getItem("permission") || JSON.stringify({})
    );
    const sportPermission = permission?.otherPermissions?.sport_permission;

    // If sport_permission is null, undefined, or empty array → full access to all sports
    if (sportPermission === null ||
        sportPermission === undefined ||
        (Array.isArray(sportPermission) && sportPermission.length === 0)) {
      return null;
    }

    return sportPermission;
  }

  return null; // No restrictions for other roles (SUPER_ADMIN, CLUB_ADMIN, etc.)
};

export const useGetSportPermissions = () => {
  const { LoginData } = useContext(MainContext);

  // If STAFF or STAFF_MANAGER, return their sport permissions
  if (LoginData?.role?.name === "STAFF") {
    const sportPermission = LoginData?.otherPermissions?.sport_permission;

    // If sport_permission is null, undefined, or empty array → full access to all sports
    if (sportPermission === null ||
        sportPermission === undefined ||
        (Array.isArray(sportPermission) && sportPermission.length === 0)) {
      return null;
    }

    return sportPermission;
  }

  return null; // No restrictions for other roles
};

export const useGetMyDetails = () => {
  let club_id = getClubId();

  const query = useQuery({
    queryKey: ["GetMyDetails"],
    queryFn: async () => {
      const response: any = await apiCaller.get(`${club_id}/admins/user/getme`);
      let res = response?.data;
      return res;
    },
    staleTime: 1000 * 60 * 60,
    retry: false,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return query;
};

//get details
export const useGetBookingsEvents = () => {
  let club_id = getClubId();
  const [sport_id, setsport_id] = useState<number>();
  const [viewType, setviewType] = useState<string>();
  const [service_id, setservice_id] = useState<number | undefined>();

  const query = useQuery({
    queryKey: ["GetBookingsEvents", sport_id, viewType, service_id],
    queryFn: async () => {
      const params: any = {};
      if (service_id !== undefined) {
        params.service_id = service_id;
      }
      const response: any = await apiCaller.get(
        `${club_id}/calendars/${viewType}/${sport_id}`, { params }
      );
      return response.data.data;
    },
    enabled: !!sport_id && !!viewType,
  });

  return { setviewType, setsport_id, setservice_id, ...query };
};

export const useGetAllServicesPrev = () => {
  let club_id = getClubId();
  const [location_id, setLocation_id] = useState<number>();
  const [date, setDate] = useState<string>();
  const [isNextDay, setIsNextDay] = useState(false);
  // GZ#3547: see useGetAllServices — the Cancelled eye asks the API for
  // auto-cancelled programs, which are `is_hide` on the row and so are never
  // returned by the default grid query.
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const query = useQuery({
    queryKey: [
      "calendar-daily-date-previuos",
      location_id,
      date,
      isNextDay,
      includeCancelled,
    ],
    // Keep the previous day's data while a new date loads so the daily calendar
    // doesn't flash its full-screen loading overlay on every date change.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response: any = await apiCaller.get(
        `${club_id}/calendars/services/${location_id}/${date}?previous_date_record=${isNextDay}${
          includeCancelled ? "&include_cancelled=true" : ""
        }`
      );
      return response.data?.data;
    },
    enabled: !!location_id && !!date && isNextDay,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setDate, setLocation_id, setIsNextDay, setIncludeCancelled, ...query };
};

export const useGetAllServices = () => {
  let club_id = getClubId();
  const [location_id, setLocation_id] = useState<number>();
  const [date, setDate] = useState<string>();
  // GZ#3547 (Stackd): a DESK-cancelled program keeps is_hide=false, so it comes
  // down with the normal day payload and the Cancelled eye can reveal it in the
  // browser. An AUTO-cancelled one is stamped is_hide=true by the <N-players
  // rule and never leaves SQL — no client-side toggle can bring back a row the
  // API did not send. Asking for it is therefore part of the toggle, and it is
  // in the queryKey so flipping the eye refetches instead of reusing the
  // narrower cached day.
  const [includeCancelled, setIncludeCancelled] = useState(false);
  /**
   * GZ#4714 — one booking the user asked for by id: the target of a staff
   * notification deep link. The grid's visibility rules legitimately keep some
   * rows out of the day payload (a cancelled Block is `is_hide`, and the
   * Cancelled eye deliberately does not resurrect those), but a staff member
   * clicking "you were @mentioned on this booking" has asked for that exact
   * row. It is sent back so the detail can open; it is still not painted.
   * In the queryKey so arriving on a deep link refetches the narrower day.
   */
  const [focusBookingId, setFocusBookingId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: [
      "calendar-daily-date",
      location_id,
      date,
      includeCancelled,
      focusBookingId,
    ],
    // Keep the previous day's data while a new date loads so the daily calendar
    // doesn't flash its full-screen loading overlay (EventLoading) on every date
    // change — isLoading stays false on subsequent dates, only true on first load.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params: Record<string, any> = {};
      if (includeCancelled) params.include_cancelled = true;
      if (focusBookingId) params.focus_booking_id = focusBookingId;
      const response: any = await apiCaller.get(
        `${club_id}/calendars/services/${location_id}/${date}`,
        Object.keys(params).length ? { params } : undefined
      );
      return response.data?.data;
    },
    enabled: !!location_id && !!date,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return {
    setDate,
    setLocation_id,
    setIncludeCancelled,
    setFocusBookingId,
    ...query,
  };
};

export const useGetAllLocationServices = () => {
  let club_id = getClubId();
  const [date, setDate] = useState<string>();

  const query = useQuery({
    queryKey: ["All-location-service", date],
    queryFn: async () => {
      const response: any = await apiCaller.get(
        `${club_id}/calendars/services/${date}/all/location_data`
      );
      return response.data?.data;
    },
    enabled: !!date,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setDate, ...query };
};


export const useGetBookedCourtTimimg = () => {
  let club_id = getClubId();
  const [location_id, setLocation_id] = useState<number>();
  const [date, setDate] = useState<string>();
  const [filteredSportIds, setFilteredSportIds] = useState<number[]>([]);
  const query = useQuery({
    queryKey: ["booked_court_timing-68758", location_id, date, filteredSportIds],
    queryFn: async () => {
      const response: any = await apiCaller.get(
        `${club_id}/calendars/booked-courts/${location_id}/${date}`,
        {
          params: {
            sportId: filteredSportIds,
          },
        }
      );
      return response.data?.data;
    },
    enabled: !!location_id && !!date,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { setDate, setLocation_id, setFilteredSportIds, ...query };
};


export const useGetAllChatCount = () => {
  let club_id = getClubId();
  const accessToken = localStorage.getItem("accessToken");
  const [service_id, setService_id] = useState<string>();

  const query = useQuery({
    queryKey: ["chatcountOpenMatch", service_id],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_URL}/${club_id}/chat-contacts/${service_id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            accessType: "admin",
          },
        }
      );
      return response.data?.data;
    },
    enabled: !!service_id,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setService_id, ...query };
};

// get all stats
export const useGetAllStats = () => {
  let club_id = getClubId();
  const [location_id, setLocation_id] = useState<number>();
  const [sports, setSports] = useState([]);
  const [date, setDate] = useState<string>();

  const queryString = sports.map((id) => `sportId=${id}`).join("&");

  const query = useQuery({
    queryKey: ["calendar-daily-stats", location_id, date, sports],
    // Keep previous stats while a new date loads to match the calendar's
    // keep-previous-data behavior and avoid a ratings flash on date change.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response: any = await apiCaller.get(
        `${club_id}/calendars/services/stats/${location_id}/${date}?${queryString}`
      );
      return response.data?.data;
    },
    enabled: !!location_id && !!date,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setDate, setLocation_id, sports, setSports, ...query };
};

// get booking details from service booking id
export const useGetParticularBooking = () => {
  let club_id = getClubId();
  const [serviceId, setServiceId] = useState<number>();

  const query = useQuery({
    queryKey: ["calendar-daily-stats", serviceId],
    queryFn: async () => {
      const response: any = await apiCaller.get(
        `${club_id}/calendars/services/${serviceId}`
      );
      return response.data?.data;
    },
    enabled: !!serviceId,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setServiceId, ...query };
};

// get all logs
const getLogs = async (service_id: number) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.get(
      `${club_id}/calendars/services/view-logs/${service_id}`
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    return err;
  }
};

// get all logs
export const GetServiceLogs = async (service_id: number) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.get(`${club_id}/services/logs/${service_id}`);
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    return err;
  }
};

// get membership logs
const getMembershipLogs = async (membershipId: number) => {
  try {
    let club_id = getClubId();
    const customer_membership_id = membershipId;
    let res = await apiCaller.get(
      `${club_id}/memberships/customer-membership/logs/${customer_membership_id}`
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    return err;
  }
};

// Activity logs for a booking. Fetched only when the Logs tab is opened, which
// remounts LogsComponent each time. Kept uncached (staleTime 0 + refetchOnMount)
// so any booking change — coach update, add/cancel player, waiting-list, score,
// etc. — is reflected the moment the Logs tab is reopened, with no hard refresh.
export const useGetServiceLogs = () => {
  let club_id = getClubId();
  const [serviceId, setServiceId] = useState<number>();

  const query = useQuery({
    queryKey: ["GetServiceLogs-popover", serviceId],
    queryFn: async () => {
      let res = await apiCaller.get(`${club_id}/services/logs/${serviceId}`);
      return res?.data?.data;
    },
    enabled: !!serviceId,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  return { setServiceId, ...query };
};

// get service by the id
const getServiceById = async (service_id: number) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.get(
      `${club_id}/calendars/services/${service_id}`
    );
    return response;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// GZ#1723: read-only live price preview while rescheduling a court booking.
// Returns { applicable, old_slot_price, new_slot_price, price_changed, players }
// for the schedule the admin is ABOUT to save; the backend writes nothing.
export const getBookingPricePreview = async (
  service_booking_id: number,
  // booking_id: the Booking product the edited schedule prices against — a duration
  // change selects a DIFFERENT product (60-min vs 120-min), and without it the backend
  // prices both sides on the old product and the preview shows no change. (GZ#1723 rd2)
  params: { date: string; start_time: string; end_time: string; courts: number[]; booking_id?: number }
) => {
  let club_id = getClubId();
  const query = new URLSearchParams({
    date: params.date,
    start_time: params.start_time,
    end_time: params.end_time,
    courts: (params.courts || []).join(","),
    ...(params.booking_id ? { booking_id: String(params.booking_id) } : {}),
  }).toString();
  const response: any = await apiCaller.get(
    `${club_id}/calendars/services/${service_booking_id}/price-preview?${query}`
  );
  return response?.data?.data;
};

// GZ#1721: read-only price quote for the CREATE booking dialog — no service booking
// exists yet, so this quotes off the selected Booking's conditional slot pricing.
// Returns { applicable, price, per_player_price }.
export const getNewBookingPriceQuote = async (params: {
  booking_id: number;
  location_id: number;
  date: string;
  start_time: string;
  end_time: string;
  courts: number[];
  booking_type?: string;
}) => {
  let club_id = getClubId();
  const query = new URLSearchParams({
    booking_id: String(params.booking_id),
    location_id: String(params.location_id),
    date: params.date,
    start_time: params.start_time,
    end_time: params.end_time,
    courts: (params.courts || []).join(","),
    booking_type: params.booking_type || "Regular",
  }).toString();
  const response: any = await apiCaller.get(
    `${club_id}/calendars/price-preview?${query}`
  );
  return response?.data?.data;
};

// GZ#4498: drag a one-court booking, lesson or event to another court and/or
// another time the same day (same length). Send only what changed. The server
// refuses a taken court or a busy coach (409) and a locked club (400).
export const moveCalendarBooking = async (
  service_booking_id: number,
  move: { court_id?: number; start_time?: string; end_time?: string; notify_players?: boolean }
) => {
  let club_id = getClubId();
  const response: any = await apiCaller.patch(
    `${club_id}/calendars/services/${service_booking_id}/move`,
    move
  );
  return response?.data;
};

// GZ#1721: read-only price quote for the CREATE dialog when a Lesson (coach-training)
// service is selected. The backend resolves the snapshot price off the chosen variant /
// coach / base lesson price, then applies any conditional time-of-day discount for the
// chosen day+slot (so peak/off-peak lesson tariffs quote correctly) before a client is
// added or the booking saved. Returns { applicable, price }.
export const getNewLessonPriceQuote = async (params: {
  lesson_id: number;
  location_id: number;
  date: string;
  start_time: string;
  end_time: string;
  lesson_variant_id?: number | null;
  coaches?: number[];
}) => {
  let club_id = getClubId();
  const query = new URLSearchParams({
    lesson_id: String(params.lesson_id),
    location_id: String(params.location_id),
    date: params.date,
    start_time: params.start_time,
    end_time: params.end_time,
    ...(params.lesson_variant_id != null
      ? { lesson_variant_id: String(params.lesson_variant_id) }
      : {}),
    ...(params.coaches && params.coaches.length > 0
      ? { coaches: params.coaches.join(",") }
      : {}),
  }).toString();
  const response: any = await apiCaller.get(
    `${club_id}/calendars/price-preview?${query}`
  );
  return response?.data?.data;
};

// get pending payments by service booking id
export const getPendingPaymentsById = async (service_id: number) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.get(
      `${club_id}/payments/services/pending-payments/${service_id}`
    );
    return response?.data?.data;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// download invoice 
const downloadInvoice = async ({ paymentIds }: { paymentIds: number[] }) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.post(
      `${club_id}/services/download-invoice`,
      { paymentIds }
    );
    return response;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// email a booking invoice PDF to a given address
const sendBookingInvoiceEmail = async ({
  paymentIds,
  email,
}: {
  paymentIds: number[];
  email: string;
}) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.post(
      `${club_id}/services/send-invoice-email`,
      { paymentIds, email }
    );
    return response;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// download a membership (bundle) purchase invoice — memberships have no payment
// row, so this is keyed on the customer_membership id, not paymentIds.
const downloadMembershipInvoice = async ({
  customerMembershipId,
}: {
  customerMembershipId: number;
}) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.post(
      `${club_id}/memberships/customer-membership/${customerMembershipId}/download-invoice`,
      {}
    );
    return response;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// email a membership (bundle) purchase invoice PDF to a given address
const sendMembershipInvoiceEmail = async ({
  customerMembershipId,
  email,
}: {
  customerMembershipId: number;
  email: string;
}) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.post(
      `${club_id}/memberships/customer-membership/${customerMembershipId}/send-invoice-email`,
      { email }
    );
    return response;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// create service
const createBooking = async (data: any) => {
  try {
    let club_id = getClubId();
    let response: any = await apiCaller.post(
      `${club_id}/calendars/services`,
      data
    );
    return response;
  } catch (error: any) {
    const err = error.response?.data?.message;
    throw err;
  }
};

// update service
const updateBookingData = async (
  service_id: number,
  service_booking_id: number,
  data: any
) => {
  try {
    let club_id = getClubId();
    let res: any = await apiCaller.put(
      `${club_id}/calendars/servics/${service_id}`,
      data
    );
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// toggle court release for a service booking
const toggleCourtRelease = async (service_booking_id: number) => {
  try {
    let club_id = getClubId();
    let res: any = await apiCaller.patch(
      `${club_id}/calendars/services/toggle-court-release/${service_booking_id}`
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

/**
 * GZ#2285 — hold ONE session at its full size, or hand it back to progressive
 * court release. Toggles service_bookings.progressive_release_exempt.
 */
const toggleProgressiveReleaseExempt = async (service_booking_id: number) => {
  try {
    let club_id = getClubId();
    let res: any = await apiCaller.patch(
      `${club_id}/calendars/services/toggle-progressive-release-exempt/${service_booking_id}`
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// delete conflict service
const deleteSchService = async (service_booking_id: number) => {
  try {
    let club_id = getClubId();
    let res: any = await apiCaller.delete(
      `${club_id}/calendars/schedule-bookings/${service_booking_id}`
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

/**
 * Calendar / POS / messages customer typeahead URL.
 *
 * GZ#4385 (Love All): `/services/customers?search=` 404s on no-match, is
 * accent-sensitive, and misses email/phone matches — so typing an existing
 * customer's email in Add Player showed "No User Found". Typed queries
 * (2+ chars) use the same `users/{club}/search/name` path as trips
 * (`useCustomerNameSearch`): first/last/email/phone/external_id,
 * case/accent-insensitive, `encodeURIComponent`. Digits are kept as-is so a
 * Privilee barcode (GZ#4603) is not stripped. Empty/1-char stays on the old
 * list-everyone endpoint so focusing Add Player with an empty box still lists
 * the directory.
 */
export function customerTypeaheadUrl(
  clubId: string | number,
  term: string
): string {
  const q = (term || "").trim();
  if (q.length >= 2) {
    return `users/${clubId}/search/name?name=${encodeURIComponent(q)}`;
  }
  return `${clubId}/services/customers?search=${encodeURIComponent(q)}`;
}

// get all customer list
export const useGetAllCustomerList = () => {
  const club_id = getClubId();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedName, setDebouncedName] = useState<string>("");

  const debouncedSetName = useCallback(
    debounce((value: string) => setDebouncedName(value), 500), // 500ms delay
    []
  );

  useEffect(() => {
    debouncedSetName(searchQuery);
  }, [searchQuery]);

  const term = (debouncedName || "").trim();
  const query = useQuery({
    queryKey: ["CustomersList", club_id, term],
    queryFn: async () => {
      const response = await apiCaller.get(customerTypeaheadUrl(club_id, term));
      return response.data.data;
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { setSearchQuery, ...query };
};

// Customer typeahead by name — used by the trips "Add participant" modal.
// Hits the dedicated name-search endpoint (matches first/last/email/phone,
// case- and accent-insensitive) which returns up to 10 rows as
// { id, name, first_name, last_name, email, phone_number, ... }. This is the
// right endpoint for a typeahead: /services/customers?search= 404s on no-match
// and is accent-sensitive (e.g. "joaquin" never finds "Joaquín"). Gated to
// >= 2 chars (the endpoint requires it). Same return shape as
// useGetAllCustomerList so it's a drop-in replacement.
export const useCustomerNameSearch = () => {
  const club_id = getClubId();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedName, setDebouncedName] = useState<string>("");

  const debouncedSetName = useCallback(
    debounce((value: string) => setDebouncedName(value), 350),
    []
  );

  useEffect(() => {
    debouncedSetName(searchQuery);
  }, [searchQuery]);

  const term = (debouncedName || "").trim();
  const query = useQuery({
    queryKey: ["CustomerNameSearch", club_id, term],
    queryFn: async () => {
      const response = await apiCaller.get(customerTypeaheadUrl(club_id, term));
      return response.data.data;
    },
    enabled: term.length >= 2,
    staleTime: 1000 * 60,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { setSearchQuery, ...query };
};

// get player score by service booking id
export const useGetScore = () => {
  const club_id = getClubId();
  const [Service_id, setService_id] = useState<number>();

  const query = useQuery({
    queryKey: ["getScore", Service_id],
    queryFn: async () => {
      const response = await apiCaller.get(
        `${club_id}/players/team-scores/${Service_id}`
      );
      return response.data.data;
    },
    enabled: !!Service_id,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { ...query, setService_id };
};

// update player score by service booking id
export const updateScore = async (Service_id: number, apiData: any) => {
  const club_id = getClubId();
  const response = await apiCaller.put(
    `${club_id}/players/team-scores/${Service_id}`,
    apiData
  );
  return response;
};

// update waiting approve / remove
export const updateWaiting = async (
  player_id: number,
  Service_id: number,
  type: string
) => {
  const club_id = getClubId();
  const response = await apiCaller.patch(
    `${club_id}/players/requests/${player_id}/${Service_id}/${type}`
  );
  return response;
};

//move to player from waiting list
export const updateMoveWaiting = async (
  waiting_id: any,
  Service_id: number,
  // GZ#4679: staff confirmed a customer with no gender on file.
  genderOverride: boolean = false
) => {
  const club_id = getClubId();
  const response = await apiCaller.patch(
    `${club_id}/players/move-player/${Service_id}/${waiting_id}${genderOverride ? "?gender_override=true" : ""}`,
    { action: "MOVE_TO_LIST" }
  );
  return response;
};

// make / remove host of an event or lesson booking (GZ#1911)
export const updateEventHost = async (
  Service_id: number,
  player_id: number,
  is_host: boolean
) => {
  const club_id = getClubId();
  const response = await apiCaller.patch(
    `${club_id}/players/host/${Service_id}/${player_id}`,
    { is_host }
  );
  return response;
};

// get all waiting approval list by service booking id
export const useGetAllWaitingApprovalList = () => {
  const club_id = getClubId();
  const [Service_id, setService_id] = useState<number>();

  const query = useQuery({
    queryKey: ["waitingapprovalList", Service_id],
    queryFn: async () => {
      const response = await apiCaller.get(
        `${club_id}/players/waiting-lists/${Service_id}`
      );
      return response.data.data;
    },
    enabled: !!Service_id,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setService_id, ...query };
};

// get all player notes
export const useGetPlayersNotes = () => {
  const club_id = getClubId();
  const [Service_id, setService_id] = useState<number>();

  const query = useQuery({
    queryKey: ["GetPlayerNotes", Service_id],
    queryFn: async () => {
      const response = await apiCaller.get(
        `${club_id}/calendars/services/view/${Service_id}`
      );
      return response.data.data;
    },
    enabled: !!Service_id,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    // GZ#2469 round 3: a walk-in attach from Check-In writes players the
    // calendar hover already shows; without a remount refetch the modal kept
    // the 5-minute cache and listed only the original booker.
    refetchOnMount: true,
  });
  return { setService_id, ...query };
};

// add player in waiting list
const UpdateServicesPlayers = async (
  playerId: number,
  type: string,
  isSend: boolean,
  refundMode?: "wallet" | "original" | "membership" | "none",
  // GZ#2271 — optional refund reason. Either an existing tag id, or free text
  // the backend creates on first use. Never both.
  refundTag?: { tagId: number | null; name: string | null },
  // GZ#4704 — Customer Search cancel: refund only the app's ladder tier and
  // hand the freed seat to the waiting list like an app cancel.
  extra?: { refundPercentage?: number; promoteWaitlist?: boolean }
) => {
  try {
    let club_id = getClubId();
    const refundQuery = refundMode ? `&refund_mode=${refundMode}` : "";
    const tagQuery = buildRefundTagQuery(refundTag);
    const percentQuery =
      typeof extra?.refundPercentage === "number" ? `&refund_percentage=${extra.refundPercentage}` : "";
    const promoteQuery = extra?.promoteWaitlist ? "&promote_waitlist=true" : "";
    let res: any = await apiCaller.patch(
      `${club_id}/calendars/services/players/${playerId}?type=${type}&is_send=${isSend}${refundQuery}${tagQuery}${percentQuery}${promoteQuery}`
    );
    return res.data.data;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// hide service from the calendars page
export const HideService = async (apidata: any) => {
  try {
    let club_id = getClubId();
    let res: any = await apiCaller.patch(
      `${club_id}/calendars/services/hide`,
      apidata
    );
    return res.data.data;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// get all players list if any
const getPlayerList = async (service_id: number) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.get(
      `${club_id}/calendars/services/players/list/${service_id}`
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    return err;
  }
};

// GZ#2904: download this session's registered players as a CSV. The backend
// names the file (it knows the event name + date), so read it back off the
// response rather than guessing here.
export const exportPlayerListCsv = async (service_booking_id: number) => {
  const club_id = getClubId();
  const response = await apiCaller.get(
    `${club_id}/calendars/services/players/list/${service_booking_id}/csv`,
    { responseType: "blob" }
  );
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(
      response.headers?.["content-disposition"],
      `players-${service_booking_id}.csv`
    ),
  };
};

// get all players list
export const useGetAllPlayerList = () => {
  const club_id = getClubId();
  const [service_id, setService_id] = useState<number>();

  const query = useQuery({
    queryKey: ["getallDoubleplayer-list", service_id],
    queryFn: async () => {
      const response = await apiCaller.get(
        `${club_id}/calendars/services/players/list/${service_id}`
      );
      return response.data.data;
    },
    enabled: !!service_id,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setService_id, ...query };
};

// get all waiting list by service booking id
export const useGetAllWaitingList = () => {
  const club_id = getClubId();
  const [service_id, setService_id] = useState<number>();

  const query = useQuery({
    queryKey: ["getallwaiting-list", service_id],
    queryFn: async () => {
      const response = await apiCaller.get(
        `${club_id}/players/waiting-lists/${service_id}`
      );
      return response.data.data;
    },
    enabled: !!service_id,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  return { setService_id, ...query };
};

/**
 * GZ#3067 — move a CANCELLED player back onto the booking's waiting list.
 *
 * One call does the whole thing: the backend takes them off the roster (so they
 * leave the Cancelled section) and queues them as `waiting`. `position` is the
 * Double-event waiting slot and is optional — the backend picks the lowest free
 * one when it is omitted or the requested slot is already held, so staff never
 * hit the "position already taken" dead end the club reported.
 */
export const MoveCancelledPlayerToWaitingList = async (
  service_booking_id: number,
  player_id: number,
  position?: number
) => {
  try {
    const club_id = getClubId();
    const res = await apiCaller.patch(
      `${club_id}/players/move-to-waiting-list/${service_booking_id}/${player_id}`,
      position ? { position } : {}
    );
    return res;
  } catch (error: any) {
    throw error.response?.data?.message || "Could not move this player to the waiting list";
  }
};

/**
 * GZ#4494 (Snow Monster): upcoming sessions of a class player's class, each
 * with spots left and whether the move would accept it.
 */
export const getMoveToSessionTargets = async (player_id: number, days = 14) => {
  try {
    const club_id = getClubId();
    const res = await apiCaller.get(`${club_id}/players/move-to-session/${player_id}?days=${days}`);
    return res?.data?.data ?? res?.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Could not load the sessions of this class";
  }
};

/** GZ#4494: move the player, with what they paid, to another session of the class. */
export const movePlayerToSession = async (player_id: number, target_service_booking_id: number, send_notification: boolean) => {
  try {
    const club_id = getClubId();
    const res = await apiCaller.patch(`${club_id}/players/move-to-session/${player_id}`, {
      target_service_booking_id,
      send_notification,
    });
    return res?.data?.data ?? res?.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Could not move this player";
  }
};

// add waiting list for team
const addWaitingTeam = async (service_id: number, data: any) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.post(
      `${club_id}/players/waiting-lists/${service_id}`,
      data
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// delete the service
// GZ#4021: returns the body so the modal can show what was refunded / held.
const deleteService = async (service_id: number, apiData: any) => {
  try {
    let club_id = getClubId();
    let res: any = await apiCaller.patch(
      `${club_id}/calendars/cancel-services`,
      apiData
    );
    return res?.data;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

export type CancelRefundPreview = {
  money_seats: number;
  money_total: number;
  pack_seats: number;
  not_refundable_seats: number;
  currency: string | null;
  has_refund_rule: boolean;
  can_refund: boolean;
};

// GZ#4021: what "Refund paid seats" would give back if these sessions were
// cancelled now. Feeds the cancel confirmation.
const getCancelRefundPreview = async (
  serviceBookingIds: number[]
): Promise<CancelRefundPreview | null> => {
  const club_id = getClubId();
  const res: any = await apiCaller.get(
    `${club_id}/calendars/cancel-services/refund-preview?service_booking_ids=${serviceBookingIds.join(",")}`
  );
  return res?.data?.data ?? null;
};

// add internal note (mentioned_user_ids drives booking-note staff notifications)
const addNotes = async (
  serviceId: number,
  data: { notes: string; mentioned_user_ids?: number[] }
) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.post(
      `${club_id}/services/notes/${serviceId}`,
      data
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// GZ#2698 — edit an internal note. Author-only server-side; the row comes back
// stamped `edited_at` so the note renders with an "(edited)" marker.
export const updateNote = async (noteId: number, notes: string) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.patch(`${club_id}/services/notes/${noteId}`, {
      notes,
    });
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// GZ#2698 — delete an internal note. Soft delete: the note stays in the list as
// a "This note was deleted" tombstone, it does not vanish from the history.
export const deleteNote = async (noteId: number) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.delete(`${club_id}/services/notes/${noteId}`);
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

// Payments share logic screen
export const UpdatePayments = async (serviceId: number, data: any) => {
  try {
    let club_id = getClubId();
    let res = await apiCaller.post(
      `${club_id}/payments/services/${serviceId}`,
      data
    );
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    throw err;
  }
};

const transformData = (data: any) => {
  const transformedData: any = {};
  data.forEach((item: any) => {
    const courtId = item.court_id;
    if (!transformedData[courtId]) {
      transformedData[courtId] = {
        court_name: item.court_name,
        court_id: courtId,
        sport: item.sport,
        occupancy: [],
      };
    }
    transformedData[courtId].occupancy.push(item);
  });
  const result = Object.values(transformedData);
  return result;
};

const getAllRecurringServices = async (booking_id: number) => {
  let club_id = getClubId();
  try {
    const response = await apiCaller.get(
      `${club_id}/calendars/get-service-booking/${booking_id}`
    );
    return response.data.data;
  } catch (error) {
  }
};

// get all service of a month
const getMonthsService = async (
  view_type: string,
  month_year: any,
  location_id: number,
  sport_id?: number,
  day_filter?: string,
  time_start?: string,
  time_end?: string,
  // GZ#4478: only the Monthly calendar's Sessions mode needs the bookings behind
  // each cell. Everything else keeps asking for the occupancy-only response.
  with_sessions?: boolean
) => {
  try {
    let club_id = getClubId();
    const params = new URLSearchParams();
    if (sport_id) params.append('sport_id', sport_id.toString());
    if (day_filter && day_filter !== 'all') params.append('day_filter', day_filter);
    if (time_start) params.append('time_start', time_start);
    if (time_end) params.append('time_end', time_end);
    if (with_sessions) params.append('with_sessions', '1');
    const queryString = params.toString() ? `?${params.toString()}` : '';
    let res = await apiCaller.get(
      `${club_id}/calendars/occupancy-rate/${location_id}/${view_type}/${month_year}${queryString}`
    );
    let data = res?.data?.data;
    if (view_type === "courts") {
      let setData = transformData(data);
      return setData;
    }
    return res;
  } catch (error: any) {
    let err = error.response?.data?.message;
    return err;
  }
};

// Search service bookings (list view)
export const useSearchServiceBookings = (searchParams: {
  search?: string;
  date_from?: string;
  date_to?: string;
  location_id?: number;
  service_type?: string;
  sport_id?: number;
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const club_id = getClubId();

  const query = useQuery({
    queryKey: ["search-service-bookings", club_id, searchParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchParams.search) params.append("search", searchParams.search);
      if (searchParams.date_from) params.append("date_from", searchParams.date_from);
      if (searchParams.date_to) params.append("date_to", searchParams.date_to);
      if (searchParams.location_id) params.append("location_id", String(searchParams.location_id));
      if (searchParams.service_type) params.append("service_type", searchParams.service_type);
      if (searchParams.sport_id) params.append("sport_id", String(searchParams.sport_id));
      if (searchParams.status) params.append("status", searchParams.status);
      if (searchParams.page) params.append("page", String(searchParams.page));
      if (searchParams.limit) params.append("limit", String(searchParams.limit));

      const response: any = await apiCaller.get(
        `${club_id}/calendars/services-search?${params.toString()}`
      );
      return response.data?.data;
    },
    enabled: (!!searchParams.date_from || !!searchParams.search) && !!club_id,
    // The List view is a live operational snapshot — it must reflect cancellations and
    // edits made elsewhere (calendar tab, another browser tab). A 2-min staleTime meant
    // switching Calendar -> List within that window served a cached copy, so a booking
    // just cancelled in the calendar still showed as "Active" in the list. Always refetch
    // on mount so opening the List tab shows current status.
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    refetchOnWindowFocus: false,
  });

  return query;
};

// Fetch ALL filtered service bookings across pages (for CSV export)
export const fetchAllServiceBookingsForExport = async (searchParams: {
  search?: string;
  date_from?: string;
  date_to?: string;
  location_id?: number;
  service_type?: string;
  status?: string;
}) => {
  const club_id = getClubId();
  const PAGE_SIZE = 100;
  const all: any[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const params = new URLSearchParams();
    if (searchParams.search) params.append("search", searchParams.search);
    if (searchParams.date_from) params.append("date_from", searchParams.date_from);
    if (searchParams.date_to) params.append("date_to", searchParams.date_to);
    if (searchParams.location_id) params.append("location_id", String(searchParams.location_id));
    if (searchParams.service_type) params.append("service_type", searchParams.service_type);
    if (searchParams.status) params.append("status", searchParams.status);
    params.append("page", String(page));
    params.append("limit", String(PAGE_SIZE));

    const response: any = await apiCaller.get(
      `${club_id}/calendars/services-search?${params.toString()}`
    );
    const data = response.data?.data;
    const rows = data?.serviceBookings || [];
    all.push(...rows);
    totalPages = data?.pagination?.totalPages || 1;
    page++;
  } while (page <= totalPages);

  return all;
};

export const useCalendarLogs = (params: {
  date?: string;
  location_id?: number;
  action_type?: string;
  user_id?: number;
  page?: number;
  limit?: number;
}) => {
  const club_id = getClubId();

  const query = useQuery({
    queryKey: ["calendar-logs", club_id, params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.date) searchParams.append("date", params.date);
      if (params.location_id) searchParams.append("location_id", String(params.location_id));
      if (params.action_type) searchParams.append("action_type", params.action_type);
      if (params.user_id) searchParams.append("user_id", String(params.user_id));
      if (params.page) searchParams.append("page", String(params.page));
      if (params.limit) searchParams.append("limit", String(params.limit));

      const response: any = await apiCaller.get(
        `${club_id}/calendars/calendar-logs?${searchParams.toString()}`
      );
      return response.data?.data;
    },
    enabled: !!params.date && !!club_id,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return query;
};

export {
  getLogs,
  getAllRecurringServices,
  createBooking,
  getMonthsService,
  getServiceById,
  updateBookingData,
  deleteSchService,
  getPlayerList,
  addWaitingTeam,
  UpdateServicesPlayers,
  deleteService,
  getCancelRefundPreview,
  addNotes,
  downloadInvoice,
  sendBookingInvoiceEmail,
  downloadMembershipInvoice,
  sendMembershipInvoiceEmail,
  getMembershipLogs,
  toggleCourtRelease,
  toggleProgressiveReleaseExempt,
};

// GZ#4695 — switch "Host pays full court" ↔ "Split between players" on an
// existing private match. Preview is read-only; apply re-prices unpaid seats.
export const previewPaymentSplit = async (serviceBookingId: number, mode: string) => {
  const club_id = getClubId();
  const res: any = await apiCaller.get(
    `${club_id}/calendars/services/${serviceBookingId}/payment-split/preview`,
    { params: { mode } }
  );
  // GZ#4977: other calendar GETs read response.data.data; this one used
  // res.data, so a wrapped body made can_change undefined and the switch
  // read-only. Unwrap either shape.
  return unwrapCalendarPayload(res);
};

export const applyPaymentSplit = async (serviceBookingId: number, mode: string) => {
  const club_id = getClubId();
  const res: any = await apiCaller.patch(
    `${club_id}/calendars/services/${serviceBookingId}/payment-split`,
    { mode }
  );
  return unwrapCalendarPayload(res);
};

// GZ#3968: send a seat's court recording to the player. channel=email sends
// it from the backend; whatsapp returns a wa.me link to open; every call is
// logged as "Recording shared" on the booking.
export type RecordingShareChannel = "copy" | "whatsapp" | "email";
export const shareRecording = async (playerId: number, channel: RecordingShareChannel) => {
  const club_id = getClubId();
  const response: any = await apiCaller.post(
    `${club_id}/calendars/services/players/${playerId}/recording-share`,
    { channel }
  );
  return response?.data?.data as {
    url: string;
    message: string;
    whatsapp_url: string | null;
    email: string | null;
    last_shared: { channel: string; by: string | null; at: string };
  };
};
